<template>
  <div class="card" style="width: 800px; height: auto; font-family: 'Lato', sans-serif;">
    <header class="card-header">
      <p class="card-header-title" style="font-size: large">
        {{ i18n.sendPaymentLink[iso] || 'Send payment link' }}
      </p>
      <button class="card-header-icon" aria-label="more options" @click="$emit('close')">
        <span class="icon">
          <i class="fas fa-times" aria-hidden="true"></i>
        </span>
      </button>
    </header>
    
    <div class="card-content">
      <div v-if="loading" class="has-text-centered p-6">
        <b-icon pack="fas" icon="circle-notch" custom-class="fa-spin" size="is-large" type="is-primary"></b-icon>
        <p class="mt-4">{{ i18n.processing[iso] || 'Processing...' }}</p>
      </div>

      <div v-else class="columns is-multiline">
        <div class="column is-12">
          <b-field :label="i18n.selectPlan[iso]" label-position="on-border">
            <b-select v-model="selectedPlan" expanded>
              <option v-for="plan in plans" :value="plan.id" :key="plan.id">
                {{ plan.name }} - ${{ plan.price }}
              </option>
            </b-select>
          </b-field>
        </div>

        <div class="column is-6">
          <b-field :label="i18n.paymentOption[iso]" label-position="on-border">
            <b-select v-model="paymentOption" expanded>
              <option value="full">{{ i18n.fullPayment[iso] }}</option>
              <option value="50">{{ i18n.down50[iso] }}</option>
              <option value="30">{{ i18n.down30[iso] }}</option>
              <option value="custom">{{ i18n.customAmount[iso] }}</option>
              <option value="discount10">{{ i18n.fullPaymentDiscount[iso] }}</option>
            </b-select>
          </b-field>
        </div>

        <div class="column is-6" v-if="paymentOption === 'custom'">
          <b-field :label="i18n.customAmount[iso]" label-position="on-border">
            <b-input type="number" v-model.number="customAmount" min="0" step="0.01" icon="dollar-sign" icon-pack="fas"></b-input>
          </b-field>
        </div>

        <div class="column is-6" v-if="paymentOption && paymentOption !== 'full' && paymentOption !== 'discount10'">
          <b-field :label="i18n.installments[iso]" label-position="on-border">
            <b-select v-model.number="installments" expanded>
              <option v-for="n in installmentOptions" :key="n" :value="n">{{ n }} {{ i18n.months[iso] }}</option>
            </b-select>
          </b-field>
        </div>

        <div class="column is-12">
          <div class="notification is-light">
            <div class="is-flex is-justify-content-space-between mb-2" v-if="paymentOption === 'discount10'">
              <span>{{ i18n.originalPrice[iso] }}:</span>
              <strong style="text-decoration: line-through; color: #888;">${{ activePlan.price.toFixed(2) }}</strong>
            </div>
            <div class="is-flex is-justify-content-space-between mb-2" v-if="paymentOption === 'discount10'">
              <span>{{ i18n.discount10[iso] }}:</span>
              <strong class="has-text-success">-${{ (activePlan.price * 0.1).toFixed(2) }}</strong>
            </div>
            <div class="is-flex is-justify-content-space-between mb-2">
              <span>{{ (paymentOption === 'full' || paymentOption === 'discount10') ? i18n.totalToPay[iso] : i18n.downPayment[iso] }}:</span>
              <strong>${{ downAmount.toFixed(2) }}</strong>
            </div>
            <div class="is-flex is-justify-content-space-between mb-2" v-if="paymentOption !== 'full' && paymentOption !== 'discount10'">
              <span>{{ i18n.financedAmount[iso] }}:</span>
              <strong>${{ financedAmount.toFixed(2) }}</strong>
            </div>
            <div class="is-flex is-justify-content-space-between" v-if="paymentOption !== 'full' && paymentOption !== 'discount10'">
              <span>{{ i18n.monthlyPayment[iso] }} ({{ installments }} {{ i18n.months[iso] }} · {{ (interestRate * 100).toFixed(1) }}% {{ i18n.monthlyInterest[iso] }}):</span>
              <strong class="has-text-info">${{ monthlyPayment.toFixed(2) }}/mo</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer class="card-footer">
      <a @click="$emit('close')" class="card-footer-item button is-danger is-outlined is-small" style="height: auto">
        <b>{{ i18n.cancel[iso] }}</b>
      </a>
      <a :class="['card-footer-item button is-success is-outlined is-small', { 'is-loading': loading }]"
         style="height: auto" @click="generateLink" :disabled="!paymentOption || (paymentOption === 'custom' && customAmount <= 0)">
        <b>{{ i18n.generateAndSendLink[iso] }}</b>
      </a>
    </footer>
  </div>
</template>

<script>
import axios from "axios";
import i18n from "../utils/i18n";

const API_URL = process.env.VUE_APP_API_URL;

export default {
  name: "ModalGeneratePaymentLink",
  props: {
    guest: { type: Object, required: true },
    iso: { type: String, default: "en" }
  },
  data() {
    return {
      i18n: i18n,
      loading: false,
      plans: [
        { id: 'vanguard', name: 'Vanguard', price: 4990, term: 99 },
        { id: 'expedition', name: 'Expedition', price: 2490, term: 15 },
        { id: 'pioneer', name: 'Pioneer', price: 1290, term: 5 }
      ],
      selectedPlan: 'vanguard',
      paymentOption: '',
      customAmount: null,
      installments: 12,
      installmentOptions: [12, 24, 36],
      interestRate: 0.179
    };
  },
  mounted() {
    if (this.guest && this.guest.Autocompletado) {
      const auto = this.guest.Autocompletado;
      
      if (auto.AnosComprados === 99 || auto.PrecioVenta === 4990 || auto.PrecioVenta === 4990 * 0.9) {
        this.selectedPlan = 'vanguard';
      } else if (auto.AnosComprados === 15 || auto.PrecioVenta === 2490 || auto.PrecioVenta === 2490 * 0.9) {
        this.selectedPlan = 'expedition';
      } else if (auto.AnosComprados === 5 || auto.PrecioVenta === 1290 || auto.PrecioVenta === 1290 * 0.9) {
        this.selectedPlan = 'pioneer';
      }

      const activePlanPrice = this.plans.find(p => p.id === this.selectedPlan)?.price || 4990;
      
      if (auto.PrecioVenta === activePlanPrice * 0.9 && auto.PagoInicial === auto.PrecioVenta) {
        this.paymentOption = 'discount10';
      } else if (auto.PrecioVenta === activePlanPrice * 0.9) {
        // Was discounted, but maybe not fully paid. Wait, discount10 in backoffice enforces full payment?
        // Ah, discount10 in backoffice means effective price = 90% and downAmount = effectivePrice!
        // But what if they financed a discounted price? The backoffice modal only defines discount10 as 100% down.
        // I will map discount10 if they paid the discounted price upfront or custom otherwise.
        if (auto.PagoInicial === auto.PrecioVenta) {
          this.paymentOption = 'discount10';
        } else {
          this.paymentOption = 'custom';
          this.customAmount = auto.PagoInicial;
        }
      } else if (auto.PagoInicial === auto.PrecioVenta) {
        this.paymentOption = 'full';
      } else if (auto.PagoInicial === auto.PrecioVenta * 0.5) {
        this.paymentOption = '50';
      } else if (auto.PagoInicial === auto.PrecioVenta * 0.3) {
        this.paymentOption = '30';
      } else {
        this.paymentOption = 'custom';
        this.customAmount = auto.PagoInicial;
      }
      
      if (auto.NumeroMensualidades) {
        this.installments = auto.NumeroMensualidades;
      }
      if (auto.Interes !== undefined && auto.Interes !== null) {
        this.interestRate = auto.Interes;
      }
    }
  },
  computed: {
    activePlan() {
      return this.plans.find(p => p.id === this.selectedPlan) || this.plans[0];
    },
    effectivePrice() {
      if (this.paymentOption === 'discount10') {
        return this.activePlan.price * 0.9;
      }
      return this.activePlan.price;
    },
    downAmount() {
      if (this.paymentOption === 'full' || this.paymentOption === 'discount10') return this.effectivePrice;
      if (this.paymentOption === '50') return this.effectivePrice * 0.5;
      if (this.paymentOption === '30') return this.effectivePrice * 0.3;
      if (this.paymentOption === 'custom') return Math.max(0, this.customAmount || 0);
      return 0;
    },
    financedAmount() {
      return Math.max(0, this.effectivePrice - this.downAmount);
    },
    monthlyPayment() {
      const P = this.financedAmount;
      if (P <= 0) return 0;
      const r = Math.pow(1 + this.interestRate, 1 / 12) - 1;
      const n = this.installments;
      return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    }
  },
  methods: {
    async generateLink() {
      if (!this.paymentOption) return;
      if (this.paymentOption === 'custom' && !(this.customAmount > 0)) return;
      
      this.loading = true;
      try {
        const isFull = this.paymentOption === 'full' || this.paymentOption === 'discount10';
        const nowTs = Date.now();
        const firstInstallmentTs = nowTs + 30 * 24 * 3600 * 1000;
        
        const autocompleteData = {
          FechaCompra: nowTs,
          PrecioVenta: this.effectivePrice,
          CostoAdministrativo: 0,
          PagoInicial: this.downAmount,
          NumeroMensualidades: isFull ? 1 : this.installments,
          Interes: this.interestRate,
          FechaPrimeraMensualidad: isFull ? null : firstInstallmentTs,
          AnosComprados: this.activePlan.term,
          MembershipType: 'Regular',
          ContractStatus: 'Pending',
          Notas: ''
        };

        // 1. Guardar Autocompletado en Prospecto
        await axios.put(`${API_URL}/prospectos/${this.guest._id}`, {
          Autocompletado: autocompleteData
        });

        // 2. Generar y Enviar Link
        const currentUrl = window.location.origin;
        await axios.post(`${API_URL}/prospectos/${this.guest._id}/process-and-checkout?mode=sendemail`, {
          successUrl: currentUrl + "/payment-success",
          cancelUrl: currentUrl + "/payment-cancel"
        });

        this.$buefy.toast.open({
          message: this.i18n.paymentLinkSentEmail[this.iso] || "Payment link sent successfully via email.",
          type: "is-success"
        });
        
        this.$emit("refresh");
        this.$emit("close");
      } catch (err) {
        console.error(err);
        this.$buefy.toast.open({
          message: err?.response?.data?.message || err.message || (this.i18n.errorGeneratingLink[this.iso] || "Error generating payment link"),
          type: "is-danger"
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
